import * as B from '@blueprintjs/core';
import React, {useMemo} from 'react';

import TilerThumbnail from 'app/components/TilerThumbnail';
import {useUserInfo} from 'app/providers/AuthProvider';
import {useFeatureDataForFeature} from 'app/providers/FeatureDataProvider';
import * as featureUtils from 'app/utils/featureUtils';
import * as imageryUtils from 'app/utils/imageryUtils';
import * as mapUtils from 'app/utils/mapUtils';

import cs from './ScenePreview.module.scss';
import {getUnsupportedScenesMessage, imageRefsAreSupported} from '../PromptView/promptConfigs';

interface Props {
  featureId: number;
  imageRefs: mapUtils.MapImageRefs;
  onEnterCompareMode: () => void;
  onExitCompareMode: () => void;
}

const ScenePreview: React.FC<Props> = ({
  featureId,
  imageRefs,
  onEnterCompareMode,
  onExitCompareMode,
}) => {
  const [organization] = useUserInfo();
  const featureData = useFeatureDataForFeature(featureId);

  const compareMode = imageRefs.length === 2;
  const scenesSupported = imageRefsAreSupported(imageRefs);

  // Mirrors the scene lookup compare mode runs on entry, so we only offer the
  // link when a second scene actually exists.
  const hasSecondScene = useMemo(
    () =>
      !!featureData &&
      featureUtils.getProcessedFeatureData(
        featureData.filter(featureUtils.makeLayerKeyPredicate(imageRefs[0].layerKey)).toList(),
        imageRefs[0].layerKey
      ).size >= 2,
    [featureData, imageRefs]
  );

  const scenes = useMemo(
    () =>
      imageRefs.map((imageRef) => {
        const featureDatum = imageRef.cursor
          ? featureData?.find(
              (datum) =>
                datum!.get('types').includes(imageRef.layerKey) &&
                datum!.get('date') === imageRef.cursor
            )
          : undefined;
        return {
          date: imageRef.cursor
            ? imageryUtils.formatDate(imageRef.cursor, imageRef.layerKey)
            : null,
          thumbnailUrl: featureDatum
            ? featureUtils.imageAndSourceDetails(featureDatum, imageRef.layerKey, organization).url
            : undefined,
        };
      }),
    [imageRefs, featureData, organization]
  );

  const dateText = scenes
    .map(({date}) => date)
    .filter(Boolean)
    .join(compareMode ? ' → ' : '');

  if (!scenesSupported) {
    return (
      <div className={cs.scenePreview}>
        <div className={cs.scenePreviewText}>
          {featureData === null ? (
            <B.Spinner size={20} />
          ) : (
            <span>{getUnsupportedScenesMessage(imageRefs)}</span>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={cs.scenePreview}>
      <div className={cs.sceneRow}>
        <div className={cs.sceneCard}>
          <div className={cs.scenePreviewThumbnails}>
            {scenes.map(({thumbnailUrl, date}, idx) => (
              <div key={idx} className={cs.sceneThumbnail}>
                {thumbnailUrl &&
                  (mapUtils.isTilerUrl(thumbnailUrl) ? (
                    <TilerThumbnail
                      className={cs.sceneThumbnailImage}
                      url={thumbnailUrl}
                      alt={date ? `Scene from ${date}` : ''}
                    />
                  ) : (
                    <img
                      className={cs.sceneThumbnailImage}
                      loading="lazy"
                      src={thumbnailUrl}
                      alt={date ? `Scene from ${date}` : ''}
                    />
                  ))}
              </div>
            ))}
          </div>
          <div className={cs.scenePreviewText}>
            <span className={cs.scenePreviewLabel}>
              {compareMode ? 'Comparing 2 scenes' : 'Scene'}
            </span>
            {dateText && <span className={cs.scenePreviewDate}>{dateText}</span>}
          </div>
          {compareMode && (
            <B.Button
              icon="cross"
              minimal
              small
              aria-label="Remove a scene"
              onClick={onExitCompareMode}
            />
          )}
        </div>
        {!compareMode && hasSecondScene && (
          <button className={cs.compareCard} onClick={onEnterCompareMode}>
            <span className={cs.compareCardTitle}>
              <B.Icon icon="plus" size={12} />
              Compare a date
            </span>
          </button>
        )}
      </div>
    </div>
  );
};

export default ScenePreview;
